import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import LoadingWidget from "../../../components/loading_spinner";
import {
  ChevronDown,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Filter,
  MoreVertical,
  Plus,
} from "react-feather";
import {
  CallHistory,
  Incidents,
} from "../../../../service/models/complaint_model";
import {
  getIncidentsTrackerList,
  updateIncidentById,
} from "../../../../service/repos/support/complaint_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useHistory } from "react-router-dom";
import { urls } from "../../../../utils/urls";
import ViewIncidentModal from "./ViewIncidentModal";
import EditIncidentModal from "./EditIncidentModal";
import ViewHistoryModal from "./ViewHistoryModal";
import EditHistoryModal from "./EditHistoryModal";
import Icon from "@mdi/react";
import { mdiRefresh } from "@mdi/js";
import { debounce } from "lodash";
import { toQueryString } from "../../../../service/api_client";
import IncidentTrackerFilters, { FilterData } from "./filter_incidents";
import Pagination from "../../../new_components/common/pagination";
import { formatDateWithOrdinal } from "../../content_panel/utils/helpers";
import { exportCSV } from "../../../../utils/csv_generator";
import dayjs from "dayjs";
import moment from "moment";

interface IncidentTrackerPageProps {}

const defaultFilters: FilterData = {
  search: "",
  complainant_name: "",
  complainant_org: "",
  complainant_state: "",
  status: "",
  complainant_number: "",
  serviceDeskId: "",
  fromDate: "",
  toDate: "",
  complaint_related_to: "",
  subcategory_of_related_to: "",
  callStatus: "",
  direction: "",
};

const defaultApiParams = {
  ...defaultFilters,
  page: 1,
  count: 100,
  sort: "",
  sort_order: 1,
};

interface ModalState<T> {
  isOpen: boolean;
  data: T | null;
}

interface EditHistoryModalState {
  isOpen: boolean;
  history: CallHistory | null;
  incidentIndex: number | null;
  historyIndex: number | null;
}

const IncidentTrackerPage: FunctionComponent<IncidentTrackerPageProps> = () => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const navigation = useHistory();

  // Data state
  const [incidents, setIncidents] = useState<Incidents[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filter state
  const [filters, setFilters] = useState<FilterData>(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [apiParams, setApiParams] = useState(defaultApiParams);

  // UI state
  const [timeStamp, setTimeStamp] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [historyDropdownOpen, setHistoryDropdownOpen] = useState<string | null>(
    null
  );

  // Modal states
  const [viewIncidentModal, setViewIncidentModal] = useState<
    ModalState<Incidents>
  >({
    isOpen: false,
    data: null,
  });
  const [editIncidentModal, setEditIncidentModal] = useState<
    ModalState<Incidents>
  >({
    isOpen: false,
    data: null,
  });
  const [viewHistoryModal, setViewHistoryModal] = useState<
    ModalState<CallHistory>
  >({
    isOpen: false,
    data: null,
  });
  const [editHistoryModal, setEditHistoryModal] =
    useState<EditHistoryModalState>({
      isOpen: false,
      history: null,
      incidentIndex: null,
      historyIndex: null,
    });

  // API call function
  const getIncidentsList = async (params: any = apiParams) => {
    setLoading(true);
    try {
      // Clean empty values
      const cleanedParams: Record<string, any> = {};
      Object.entries(params).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          cleanedParams[key] = value;
        }
      });

      const query = toQueryString(cleanedParams);
      const response = await getIncidentsTrackerList(query);

      if (response.success) {
        setTimeStamp(response.timeStamp);

        setIncidents(response.data || []);
        setTotalCount(response?.metadata?.total);
      } else {
        showToast({ type: ToastType.error, text: "Failed to load list..." });
      }
    } catch (error) {
      console.error("Error fetching incident list:", error);
      showToast({ type: ToastType.error, text: "Failed to load list..." });
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      const newParams = {
        ...apiParams,
        ...filters,
        search: searchValue,
        page: 1,
      };
      setApiParams(newParams);
      getIncidentsList(newParams);
    }, 500),
    [apiParams, filters]
  );

  // Filter handlers
  const handleFilterChange = (field: keyof FilterData, value: string) => {
    let formattedValue: string | undefined = value;

    // Special handling for dates to avoid UTC shift
    if (field === "fromDate" && value) {
      const [year, month, day] = value.split("-").map(Number);
      const startOfDay = new Date(year, month - 1, day, 0, 0, 0, 0); // local midnight
      formattedValue = startOfDay.toISOString();
    }

    if (field === "toDate" && value) {
      const [year, month, day] = value.split("-").map(Number);
      const endOfDay = new Date(year, month - 1, day, 23, 59, 59, 999); // local end of day
      formattedValue = endOfDay.toISOString();
    }

    // Always update UI filters with raw value (so inputs show correctly)
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Debounced search
    if (field === "search") {
      debouncedSearch(value);
    }

    // Auto-apply filters for specific fields
    if (
      field === "fromDate" ||
      field === "toDate" ||
      field === "status" ||
      field === "complaint_related_to" ||
      field === "subcategory_of_related_to" ||
      field === "callStatus" ||
      field === "direction" ||
      field === "complainant_state"
    ) {
      const newParams = {
        ...apiParams,
        ...filters,
        [field]: formattedValue, // use formattedValue for API
        page: 1,
      };
      setApiParams(newParams);
      getIncidentsList(newParams);
    }
  };

  const handleApplyFilter = (field: keyof FilterData) => {
    const newParams = {
      ...apiParams,
      ...filters,
      page: 1,
    };
    setApiParams(newParams);
    getIncidentsList(newParams);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setApiParams(defaultApiParams);
    getIncidentsList(defaultApiParams);
  };

  const handleRefresh = () => {
    const refreshParams = {
      ...apiParams,
      ...filters,
    };
    getIncidentsList(refreshParams);
  };

  // Row expansion
  const toggleRow = (complainantNumber: string): void => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(complainantNumber)) {
      newExpanded.delete(complainantNumber);
    } else {
      newExpanded.add(complainantNumber);
    }
    setExpandedRows(newExpanded);
  };

  // Complaint ticket creation
  const handleCreateComplaintTicket = (incident: Incidents) => {
    const updatedIncident = {
      ...incident,
      status: "Escalated",
    };

    handleSaveIncident(updatedIncident);

    // navigation.push(urls.create_complaint, {
    //   source: "service_desk_tracker",
    //   complainant_number: incident.complainant_number,
    //   complainant_name: incident.complainant_name,
    //   incident_id: incident.serviceDeskId,
    //   complainant_org: incident.complainant_org,
    //   complaint_related_to: incident.complaint_related_to,
    //   subcategory_of_related_to: incident.subcategory_of_related_to,
    // });
    const queryParams = new URLSearchParams({
      source: "service_desk_tracker",
      complainant_number: incident.complainant_number ?? "",
      complainant_name: incident.complainant_name ?? "",
      incident_id: incident.serviceDeskId ?? "",
      complainant_org: incident.complainant_org ?? "",
      complaint_related_to: incident.complaint_related_to ?? "",
      subcategory_of_related_to: incident.subcategory_of_related_to ?? "",
    }).toString();

    window.open(`${urls.create_complaint}?${queryParams}`, "_blank");
  };

  // Action handlers
  const handleIncidentAction = (
    action: "view" | "edit" | "create",
    incident: Incidents,
    index: number
  ): void => {
    setDropdownOpen(null);

    switch (action) {
      case "view":
        setViewIncidentModal({ isOpen: true, data: incident });
        break;
      case "edit":
        setEditIncidentModal({ isOpen: true, data: incident });
        break;
      case "create":
        handleCreateComplaintTicket(incident);
        break;
    }
  };

  const handleHistoryAction = (
    action: "view" | "edit",
    history: CallHistory,
    incidentIndex: number,
    historyIndex: number
  ): void => {
    setHistoryDropdownOpen(null);

    switch (action) {
      case "view":
        setViewHistoryModal({ isOpen: true, data: history });
        break;
      case "edit":
        setEditHistoryModal({
          isOpen: true,
          history,
          incidentIndex,
          historyIndex,
        });
        break;
    }
  };

  // Save handlers
  const handleSaveIncident = async (updatedIncident: Incidents) => {
    setLoading(true);
    try {
      const response = await updateIncidentById(
        updatedIncident.serviceDeskId,
        updatedIncident
      );

      if (response.success) {
        const refreshParams = {
          ...apiParams,
          ...filters,
        };
        getIncidentsList(refreshParams);
      } else {
        showToast({
          type: ToastType.error,
          text: "Failed to update incident...",
        });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "Failed to update incident...",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHistory = async (updatedHistory: CallHistory) => {
    const { incidentIndex, historyIndex } = editHistoryModal;
    if (incidentIndex !== null && historyIndex !== null) {
      const newIncidents = [...incidents];
      newIncidents[incidentIndex].call_history[historyIndex] = updatedHistory;
      const updatedIncident = newIncidents[incidentIndex];

      try {
        setLoading(true);
        const response = await updateIncidentById(
          updatedIncident.serviceDeskId,
          updatedIncident
        );

        if (response.success) {
          const refreshParams = {
            ...apiParams,
            ...filters,
          };
          getIncidentsList(refreshParams);
        } else {
          showToast({
            type: ToastType.error,
            text: "Failed to update history...",
          });
        }
      } catch (error) {
        showToast({
          type: ToastType.error,
          text: "Failed to update history...",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Pagination
  const handlePageChange = (newPage: number) => {
    const formattedParams = {
      ...apiParams,
      ...filters,
      page: newPage,
    };

    if (filters.fromDate) {
      const [y, m, d] = filters.fromDate.split("-").map(Number);
      formattedParams.fromDate = new Date(
        y,
        m - 1,
        d,
        0,
        0,
        0,
        0
      ).toISOString();
    }
    if (filters.toDate) {
      const [y, m, d] = filters.toDate.split("-").map(Number);
      formattedParams.toDate = new Date(
        y,
        m - 1,
        d,
        23,
        59,
        59,
        999
      ).toISOString();
    }

    setApiParams(formattedParams);
    getIncidentsList(formattedParams);
  };

  const handleExportClick = async () => {
    try {
      setLoading(true);

      const query = toQueryString({ ...apiParams, all: true });
      const response = await getIncidentsTrackerList(query);

      if (!response.success || !response.data) {
        showToast({ type: ToastType.error, text: "Failed to export list..." });
        return;
      }

      const incidents = response.data;

      if (!incidents.length) {
        showToast({ type: ToastType.error, text: "No data to export." });
        return;
      }

      // Define headers
      const headers = [
        "complainant_number",
        "serviceDeskId",
        "status",
        "remark",
        "dateIncidentCreatedAt",
        "complainant_name",
        "complainant_org",
        "complaint_related_to",
        "subcategory_of_related_to",
        "followupIncidentId",
        "cmiuuid",
        "callStatus",
        "direction",
        "answered_by",
        "call_remark",
        "dateReportedAt",
        "receiver_number",
      ];

      const csvRows: string[] = [];
      csvRows.push(headers.join(",")); // add header row

      incidents.forEach((incident: any) => {
        const baseRow = [
          incident.complainant_number ?? "",
          incident.serviceDeskId ?? "",
          incident.status ?? "",
          incident.remark ?? "",
          incident.dateIncidentCreatedAt
            ? dayjs(incident.dateIncidentCreatedAt).format(
                "YYYY-MM-DD HH:mm:ss"
              )
            : "",
          incident.complainant_name ?? "",
          incident.complainant_org ?? "",
          incident.complaint_related_to ?? "",
          incident.subcategory_of_related_to ?? "",
        ];

        if (incident.call_history?.length) {
          incident.call_history.forEach((call: any) => {
            const row = [
              ...baseRow,
              call.followupIncidentId ?? "",
              call.cmiuuid ?? "",
              call.callStatus ?? "",
              call.direction ?? "",
              call.answered_by ?? "",
              call.remark ?? "",
              call.dateReportedAt
                ? dayjs(call.dateReportedAt).format("YYYY-MM-DD HH:mm:ss")
                : "",
              call.receiver_number ?? "",
            ];
            csvRows.push(row.join(","));
          });
        } else {
          // if no call history, still export one row
          csvRows.push([...baseRow, "", "", "", "", "", "", ""].join(","));
        }
      });

      const csvString = csvRows.join("\n");

      // Download CSV
      exportCSV(csvString, `incidents_export_${Date.now()}`);
      showToast({ type: ToastType.success, text: "Exported successfully!" });
    } catch (error) {
      console.error("Export error:", error);
      showToast({ type: ToastType.error, text: "Export failed!" });
    } finally {
      setLoading(false);
    }
  };

  // Initial load and cleanup
  useEffect(() => {
    getIncidentsList();

    const handleClickOutside = () => {
      setDropdownOpen(null);
      setHistoryDropdownOpen(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-1">
          <h1 className="font-bold text-2xl text-gray-500 flex items-center gap-3">
            Service Desk Tracker{" "}
            <button id="incident_tracker_refresh_btn" onClick={handleRefresh}>
              <Icon path={mdiRefresh} size={1} />
            </button>
          </h1>
          <p className="text-xs text-gray-400">
            Fetched at : {moment(timeStamp).format("hh:mm:ss A")}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            id="incident_tracker_filters_toggle_btn"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-2 py-1 rounded-md bg-myPrimaryColor hover:scale-105 duration-200 text-white"
          >
            Filters <Filter size={16} />
          </button>

          <button
            id="incident_tracker_export_btn"
            onClick={() => handleExportClick()}
            className="flex items-center gap-2 px-2 py-1 rounded-md bg-myPrimaryColor hover:scale-105 duration-200 text-white"
          >
            Export <Download size={16} />
          </button>
        </div>
      </div>
      <IncidentTrackerFilters
        filters={filters}
        showFilters={showFilters}
        loading={loading}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        onApplyFilter={handleApplyFilter}
      />

      {/* Table */}
      <div id="incident_tracker_table" className="border rounded-lg  overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900 w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Complainant Number
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Service Desk ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Complaint ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Complainant Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Org Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Complainant State
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Complaint Related To
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Sub-category Related To
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Remark
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {incidents?.map((incident, index) => (
              <React.Fragment key={incident.serviceDeskId}>
                <tr id={`incident_tracker_row_${incident.serviceDeskId}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {incident.call_history.length > 0 && (
                      <button
                        id={`incident_tracker_row_${incident.serviceDeskId}_expand_btn`}
                        onClick={() => toggleRow(incident.serviceDeskId)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expandedRows.has(incident.serviceDeskId) ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>
                    )}
                  </td>
                  <td
                    className="px-4 py-3 text-xs text-blue-600 font-medium cursor-pointer hover:text-blue-800"
                    onClick={() => toggleRow(incident.serviceDeskId)}
                  >
                    {incident.complainant_number}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident.serviceDeskId}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident?.complaintId ?? "--"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident?.complainant_name ?? "--"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident?.complainant_org ?? "--"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident?.complainant_state ?? "--"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident?.complaint_related_to ?? "--"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900">
                    {incident?.subcategory_of_related_to ?? "--"}
                  </td>{" "}
                  <td className="px-4 py-3 text-xs text-gray-900 max-w-xs break-words">
                    {incident.remark?.length > 80
                      ? `${incident.remark.slice(0, 80)}...`
                      : incident.remark}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        incident.status.toLowerCase() === "open"
                          ? "bg-yellow-100 text-yellow-800"
                          : incident.status.toLowerCase() === "resolved"
                          ? "bg-green-100 text-green-800"
                          : incident.status.toLowerCase() === "escalated"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {incident.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-900 relative">
                    <button
                      id={`incident_tracker_row_${incident.serviceDeskId}_actions_btn`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(dropdownOpen === index ? null : index);
                      }}
                      className="text-gray-500 hover:text-gray-600"
                    >
                      <MoreVertical size={20} />
                    </button>
                    {dropdownOpen === index && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-10">
                        <button
                          id={`incident_tracker_row_${incident.serviceDeskId}_view_btn`}
                          onClick={() =>
                            handleIncidentAction("view", incident, index)
                          }
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100"
                        >
                          <Eye size={14} /> View
                        </button>
                        <button
                          id={`incident_tracker_row_${incident.serviceDeskId}_edit_btn`}
                          onClick={() =>
                            handleIncidentAction("edit", incident, index)
                          }
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100"
                        >
                          <Edit size={14} /> Edit
                        </button>
                        <button
                          id={`incident_tracker_row_${incident.serviceDeskId}_create_complaint_btn`}
                          onClick={() =>
                            handleIncidentAction("create", incident, index)
                          }
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-xs hover:bg-gray-100"
                        >
                          <Plus size={14} /> Create Complaint
                        </button>
                      </div>
                    )}
                  </td>
                </tr>

                {/* Expanded Row - Call History */}
                {expandedRows.has(incident.serviceDeskId) &&
                  incident.call_history.length > 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-4 py-0 border border-t-0 border-gray-300"
                      >
                        <div className="bg-gray-50 p-4 border-t overflow-visible">
                          <h4 className="font-medium mb-3 text-gray-700">
                            Call History
                          </h4>
                          <div className="overflow-x-visible">
                            <table className="w-full text-xs table-auto border-collapse">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Followup ID
                                  </th>
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Answered By
                                  </th>
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Call Type
                                  </th>
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Call Status
                                  </th>
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Date Reported
                                  </th>
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Remark
                                  </th>
                                  <th className="text-left py-2 px-3 border border-gray-300">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {incident.call_history.map(
                                  (history, historyIndex) => (
                                    <tr
                                      id={`incident_tracker_history_row_${history.followupIncidentId}`}
                                      key={history.followupIncidentId}
                                      className="border-b border-gray-200"
                                    >
                                      <td className="py-2 px-3 border border-gray-300">
                                        {history.followupIncidentId}
                                      </td>
                                      <td className="py-2 px-3 border border-gray-300">
                                        {history.answered_by}
                                      </td>
                                      <td
                                        className="py-2 px-3 border border-gray-300"
                                        style={{ textTransform: "capitalize" }}
                                      >
                                        {history.direction}
                                      </td>
                                      <td
                                        className="py-2 px-3 border border-gray-300"
                                        style={{ textTransform: "capitalize" }}
                                      >
                                        {history.callStatus}
                                      </td>
                                      <td className="py-2 px-3 border border-gray-300">
                                        {formatDateWithOrdinal(
                                          history.dateReportedAt
                                        )}
                                      </td>
                                      <td className="py-2 px-3 max-w-xs break-words border border-gray-300">
                                        {history.remark?.length > 80
                                          ? `${history.remark.slice(
                                              0,
                                              80
                                            )}...                                        `
                                          : history.remark}
                                      </td>
                                      <td className="py-2 px-3 relative border border-gray-300">
                                        <button
                                          id={`incident_tracker_history_row_${history.followupIncidentId}_actions_btn`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setHistoryDropdownOpen(
                                              historyDropdownOpen ===
                                                `${index}-${historyIndex}`
                                                ? null
                                                : `${index}-${historyIndex}`
                                            );
                                          }}
                                          className="text-gray-500 hover:text-gray-600"
                                        >
                                          <MoreVertical size={20} />
                                        </button>
                                        {historyDropdownOpen ===
                                          `${index}-${historyIndex}` && (
                                          <div className="absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-20">
                                            <button
                                              id={`incident_tracker_history_row_${history.followupIncidentId}_view_btn`}
                                              onClick={() =>
                                                handleHistoryAction(
                                                  "view",
                                                  history,
                                                  index,
                                                  historyIndex
                                                )
                                              }
                                              className="flex items-center gap-2 w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                                            >
                                              <Eye size={12} /> View
                                            </button>
                                            <button
                                              id={`incident_tracker_history_row_${history.followupIncidentId}_edit_btn`}
                                              onClick={() =>
                                                handleHistoryAction(
                                                  "edit",
                                                  history,
                                                  index,
                                                  historyIndex
                                                )
                                              }
                                              className="flex items-center gap-2 w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                                            >
                                              <Edit size={12} /> Edit
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* No incidents found message */}
      {incidents.length === 0 && (
        <div className="text-center text-gray-500 py-6">
          No incidents found for the applied filters.
        </div>
      )}

      {incidents && incidents.length > 0 && (
        <div id="incident_tracker_pagination" className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2">
          <Pagination
            itemCount={apiParams.count}
            page={apiParams.page}
            total={totalCount}
            count={apiParams.count}
            onChange={(page) => handlePageChange(page)}
          />
        </div>
      )}

      {/* Modals */}
      <ViewIncidentModal
        incident={viewIncidentModal.data}
        isOpen={viewIncidentModal.isOpen}
        onClose={() => setViewIncidentModal({ isOpen: false, data: null })}
      />

      <EditIncidentModal
        incident={editIncidentModal.data}
        isOpen={editIncidentModal.isOpen}
        onClose={() => setEditIncidentModal({ isOpen: false, data: null })}
        onSave={handleSaveIncident}
      />

      <ViewHistoryModal
        history={viewHistoryModal.data}
        isOpen={viewHistoryModal.isOpen}
        onClose={() => setViewHistoryModal({ isOpen: false, data: null })}
      />

      <EditHistoryModal
        history={editHistoryModal.history}
        isOpen={editHistoryModal.isOpen}
        onClose={() =>
          setEditHistoryModal({
            isOpen: false,
            history: null,
            incidentIndex: null,
            historyIndex: null,
          })
        }
        onSave={handleSaveHistory}
      />

      <LoadingWidget loading={loading} />
    </div>
  );
};

export default IncidentTrackerPage;
